import React, { useState, useMemo } from 'react';
import { useOrder } from '../../context/OrderContext';
import { validateNinfaCode } from '../../utils/validators';
import { formatProductCode, normalizeProfileType } from '../../utils/formatters';

const ProfileSelector = ({ onSelect }) => {
  const [ninfaNumber, setNinfaNumber] = useState('');
  const [isScale, setIsScale] = useState(false);
  const [showScaleOption, setShowScaleOption] = useState(false);

  const profiles = [
    {
      id: '50',
      name: '50N',
      image: '/images/profiles/N50N.png'
    },
    {
      id: '56',
      name: '56',
      image: '/images/profiles/N56.png'
    },
    {
      id: '6',
      name: '6',
      image: '/images/profiles/N6.png'
    },
    {
      id: '6S',
      name: '6 STADIO',
      image: '/images/profiles/N6-STADIO.png'
    },
    {
      id: '106',
      name: '106',
      image: '/images/profiles/N106.png'
    },
    {
      id: '116',
      name: '116',
      image: '/images/profiles/N116.png'
    },
    {
      id: '176',
      name: '176',
      image: '/images/profiles/N176.png'
    },
    {
      id: '186',
      name: '186',
      image: '/images/profiles/N186.png'
    }
  ];

  const handleProfileSelect = (profileId) => {
    // Reset states
    setNinfaNumber('');
    setShowScaleOption(profileId === '186');
    setIsScale(false);

    // Format profile code with N prefix (e.g., '6' -> 'N6', '6S' -> 'N6S')
    const formattedCode = `N${profileId}`;

    onSelect({
      profileType: profileId,
      ninfaNumber: profileId, // Use profile ID as ninfa number
      isScale: false,
      code: formattedCode
    });
  };

  const handleNinfaNumberChange = (e) => {
    const value = e.target.value;
    if (value === '' || (validateNinfaCode(`N${value}`) && Number(value) > 0)) {
      setNinfaNumber(value);
      if (value) {
        onSelect({
          profileType: null,
          ninfaNumber: value,
          isScale: false
        });
      }
    }
  };

  const handleScaleChange = (e) => {
    const scaleValue = e.target.checked;
    setIsScale(scaleValue);

    // For N186, we always use the profile ID as the ninfa number
    onSelect({
      profileType: '186',
      ninfaNumber: '186',
      isScale: scaleValue,
      code: 'N186' // Base code is always N186, additional formatting handled by parent
    });
  };

  return (
    <div className="px-2 sm:px-4">
      <h1 className="text-xl font-bold text-center mb-6">
        SCEGLI IL PROFILO NINFA
      </h1>

      {/* Profile grid - no internal scrolling */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-6 mb-6">
        {profiles.map((profile) => (
          <button
            key={profile.id}
            onClick={() => handleProfileSelect(profile.id)}
            className={`
              p-3 sm:p-4 lg:p-6 bg-white rounded-xl shadow hover:shadow-lg transition-all duration-200 
              flex flex-col justify-between min-h-[140px] sm:min-h-[180px] lg:min-h-[220px]
              hover:scale-105 active:scale-95
              ${!ninfaNumber && profile.id === '186' && isScale ? 'ring-2 ring-red-500' : ''}
            `}
          >
            <div className="aspect-square relative mb-2 sm:mb-4 flex items-center justify-center">
              <img
                src={profile.image}
                alt={`Profilo N${profile.name}`}
                className="w-full h-full object-contain max-h-[80px] sm:max-h-[120px] lg:max-h-[160px]"
              />
            </div>
            <div className="flex justify-center items-center h-8 sm:h-9 lg:h-10 border border-gray-300 rounded w-full">
              <span className="font-medium text-sm sm:text-base lg:text-lg px-2">{profile.name}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Scale option for 186 */}
      {showScaleOption && !ninfaNumber && (
        <div className="flex justify-center mb-6">
          <label className="flex items-center space-x-3 bg-white p-3 rounded-lg shadow">
            <input
              type="checkbox"
              checked={isScale}
              onChange={handleScaleChange}
              className="w-4 h-4 rounded text-red-500"
            />
            <span className="text-sm sm:text-base font-medium text-gray-700">
              In scala
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

export default ProfileSelector;
